import { Box, Typography, Paper, Chip, Divider, Tabs, Tab } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import MemoryIcon from '@mui/icons-material/Memory';

const OutputPanel = ({ outputTab, setOutputTab, submissionResults, isLoading }) => {
  
  const renderOutput = () => {
    if (isLoading) {
      return (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary">Running code...</Typography>
        </Box>
      );
    }

    if (!submissionResults) {
      return (
        <Box sx={{ p: 3 }}>
          <Typography color="text.secondary" sx={{ fontSize: '0.9rem' }}>
            Click "Run Code" to test with the first example case...
          </Typography>
        </Box>
      );
    }

    // Handle error case
    if (submissionResults.status === 'error') {
      return (
        <Box sx={{ p: 2 }}>
          {/* Error Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <ErrorIcon sx={{ color: 'error.main', fontSize: 24 }} />
            <Typography variant="h6" sx={{ color: 'error.main', fontWeight: 'bold' }}>
              {submissionResults.error_type || 'Runtime Error'}
            </Typography>
          </Box>

          {/* Error Message */}
          <Paper 
            variant="outlined" 
            sx={{ 
              p: 2, 
              bgcolor: 'rgba(211, 47, 47, 0.05)',
              borderColor: 'error.main',
              borderLeft: '4px solid',
              borderLeftColor: 'error.main'
            }}
          >
            <Typography 
              variant="body2" 
              sx={{ 
                fontFamily: 'Consolas, Monaco, monospace',
                whiteSpace: 'pre-wrap',
                color: 'error.dark',
                fontSize: '0.85rem'
              }}
            >
              {submissionResults.error_message}
            </Typography>
          </Paper>

          {/* Test Case Info (if available) */}
          {submissionResults.test_case && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Input:
              </Typography>
              <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'grey.50' }}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontFamily: 'Consolas, Monaco, monospace',
                    fontSize: '0.85rem'
                  }}
                >
                  {submissionResults.test_case.input}
                </Typography>
              </Paper>
            </Box>
          )}

          {/* Stderr (if available) */}
          {submissionResults.stderr && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Error Details:
              </Typography>
              <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'grey.50' }}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontFamily: 'Consolas, Monaco, monospace',
                    fontSize: '0.75rem',
                    color: 'text.secondary',
                    whiteSpace: 'pre-wrap'
                  }}
                >
                  {submissionResults.stderr}
                </Typography>
              </Paper>
            </Box>
          )}
        </Box>
      );
    }

    // Handle success case - single test
    if (submissionResults.status === 'success' && submissionResults.test_case) {
      const { test_case } = submissionResults;
      
      return (
        <Box sx={{ p: 2 }}>
          {/* Success/Failure Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            {test_case.passed ? (
              <>
                <CheckCircleIcon sx={{ color: 'success.main', fontSize: 24 }} />
                <Typography variant="h6" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                  Accepted
                </Typography>
              </>
            ) : (
              <>
                <ErrorIcon sx={{ color: 'error.main', fontSize: 24 }} />
                <Typography variant="h6" sx={{ color: 'error.main', fontWeight: 'bold' }}>
                  Wrong Answer
                </Typography>
              </>
            )}
          </Box>

          {/* Test Case Details */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Input:
            </Typography>
            <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'grey.50', mb: 2 }}>
              <Typography 
                variant="body2" 
                sx={{ fontFamily: 'Consolas, Monaco, monospace', fontSize: '0.85rem' }}
              >
                {test_case.input}
              </Typography>
            </Paper>

            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Output:
            </Typography>
            <Paper 
              variant="outlined" 
              sx={{ 
                p: 1.5, 
                bgcolor: test_case.passed ? 'rgba(76, 175, 80, 0.05)' : 'rgba(211, 47, 47, 0.05)',
                borderLeftColor: test_case.passed ? 'success.main' : 'error.main',
                borderLeft: '4px solid',
                mb: 2
              }}
            >
              <Typography 
                variant="body2" 
                sx={{ 
                  fontFamily: 'Consolas, Monaco, monospace',
                  fontSize: '0.85rem',
                  color: test_case.passed ? 'success.dark' : 'error.dark'
                }}
              >
                {JSON.stringify(test_case.actual_output)}
              </Typography>
            </Paper>

            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Expected:
            </Typography>
            <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'grey.50' }}>
              <Typography 
                variant="body2" 
                sx={{ fontFamily: 'Consolas, Monaco, monospace', fontSize: '0.85rem' }}
              >
                {JSON.stringify(test_case.expected_output)}
              </Typography>
            </Paper>
          </Box>

          {/* Execution Stats */}
          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'flex', gap: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <AccessTimeIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">
                Runtime: {submissionResults.execution_time || 'N/A'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <MemoryIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">
                Memory: {submissionResults.memory_used || 'N/A'}
              </Typography>
            </Box>
          </Box>

          {/* Stdout (if any) */}
          {submissionResults.stdout && submissionResults.stdout !== test_case.actual_output && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Console Output:
              </Typography>
              <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'grey.50' }}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontFamily: 'Consolas, Monaco, monospace',
                    fontSize: '0.75rem',
                    whiteSpace: 'pre-wrap'
                  }}
                >
                  {submissionResults.stdout}
                </Typography>
              </Paper>
            </Box>
          )}
        </Box>
      );
    }

    return null;
  };

  const renderTestResults = () => {
    if (isLoading) {
      return (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary">Running all tests...</Typography>
        </Box>
      );
    }

    if (!submissionResults || !submissionResults.test_results) {
      return (
        <Box sx={{ p: 3 }}>
          <Typography color="text.secondary">
            Click "Run Tests" to test against all cases...
          </Typography>
        </Box>
      );
    }

    const { test_results, passed, total_tests, all_passed } = submissionResults;

    return (
      <Box sx={{ p: 2 }}>
        {/* Summary Header */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          mb: 3,
          p: 2,
          bgcolor: all_passed ? 'rgba(76, 175, 80, 0.1)' : 'rgba(255, 152, 0, 0.1)',
          borderRadius: 1
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {all_passed ? (
              <CheckCircleIcon sx={{ color: 'success.main', fontSize: 28 }} />
            ) : (
              <WarningIcon sx={{ color: 'warning.main', fontSize: 28 }} />
            )}
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              {all_passed ? 'All Tests Passed!' : 'Some Tests Failed'}
            </Typography>
          </Box>
          <Chip 
            label={`${passed}/${total_tests} Passed`}
            color={all_passed ? 'success' : 'warning'}
            sx={{ fontWeight: 'bold' }}
          />
        </Box>

        {/* Individual Test Results */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {test_results.map((result, idx) => (
            <Paper 
              key={idx}
              elevation={0}
              variant="outlined"
              sx={{ 
                p: 2,
                borderLeft: '4px solid',
                borderLeftColor: result.passed ? 'success.main' : 'error.main',
                bgcolor: result.passed 
                  ? 'rgba(76, 175, 80, 0.03)' 
                  : 'rgba(211, 47, 47, 0.03)',
                transition: 'all 0.2s',
                '&:hover': {
                  boxShadow: 1
                }
              }}
            >
              {/* Test Header */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {result.passed ? (
                    <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />
                  ) : (
                    <ErrorIcon sx={{ color: 'error.main', fontSize: 20 }} />
                  )}
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    Test Case {result.test_number}
                    {result.is_hidden && (
                      <Chip 
                        label="Hidden" 
                        size="small" 
                        sx={{ ml: 1, height: 20, fontSize: '0.65rem' }}
                      />
                    )}
                  </Typography>
                </Box>
                
                {result.execution_time && (
                  <Typography variant="caption" color="text.secondary">
                    {result.execution_time}
                  </Typography>
                )}
              </Box>

              {/* Test Details */}
              <Box sx={{ pl: 3.5 }}>
                <Typography 
                  variant="caption" 
                  color="text.secondary"
                  sx={{ 
                    display: 'block',
                    fontFamily: 'Consolas, Monaco, monospace',
                    mb: 0.5
                  }}
                >
                  Input: {result.input}
                </Typography>

                {result.error ? (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 'bold' }}>
                      {result.error_type || 'Error'}:
                    </Typography>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        display: 'block',
                        color: 'error.dark',
                        fontFamily: 'Consolas, Monaco, monospace',
                        mt: 0.5,
                        fontSize: '0.75rem'
                      }}
                    >
                      {result.error}
                    </Typography>
                  </Box>
                ) : (
                  <>
                    {!result.passed && (
                      <>
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            display: 'block',
                            fontFamily: 'Consolas, Monaco, monospace',
                            color: 'error.dark',
                            mt: 0.5
                          }}
                        >
                          Output: {JSON.stringify(result.actual_output)}
                        </Typography>
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            display: 'block',
                            fontFamily: 'Consolas, Monaco, monospace',
                            color: 'text.secondary',
                            mt: 0.5
                          }}
                        >
                          Expected: {JSON.stringify(result.expected_output)}
                        </Typography>
                      </>
                    )}
                  </>
                )}
              </Box>
            </Paper>
          ))}
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ 
      height: '100%', 
      display: 'flex',
      flexDirection: 'column',
      bgcolor: 'background.paper'
    }}>
      {/* Tabs */}
      <Tabs 
        value={outputTab} 
        onChange={(e, newValue) => setOutputTab(newValue)}
        sx={{ 
          borderBottom: 1, 
          borderColor: 'divider',
          minHeight: 40,
          '& .MuiTab-root': {
            minHeight: 40,
            py: 1
          }
        }}
      >
        <Tab label="Output" />
        <Tab label="Test Results" />
      </Tabs>

      {/* Content */}
      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        {outputTab === 0 ? renderOutput() : renderTestResults()}
      </Box>
    </Box>
  );
};

export default OutputPanel;